import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const OralCancerScreening = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Oral Cancer Screening Fort Worth TX | Early Detection | Fort Worth Dental</title>
        <meta name="description" content="Oral cancer screening in Fort Worth TX. Early detection saves lives. Comprehensive oral cancer exams as part of regular dental checkups. Call (817) 985-3337." />
        <meta name="keywords" content="oral cancer screening fort worth, mouth cancer detection fort worth tx, oral cancer exam, cancer screening dentist" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examines.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Oral Cancer Screening in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Early Detection Saves Lives</p>
            <p className="text-sm mt-4 opacity-90">Oral Cancer Exam | Early Detection | Cancer Prevention</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Why Do I Need Screening */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/oral-cancer-new-1.jpg" 
                  alt="Oral Cancer Screening" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Why Do I Need An Oral Cancer Screening?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Oral cancer affects tens of thousands of Americans each year. The statistics are sobering: approximately 54,000 people will be diagnosed with oral cancer this year in the United States, and about 9,750 will die from the disease. That's one person every hour of every day.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  However, when oral cancer is detected early, the five-year survival rate is 80-90%. This dramatic improvement in survival rates makes regular oral cancer screenings one of the most important preventive measures you can take for your health. At Fort Worth Dental, we include oral cancer screenings as part of every routine dental examination.
                </p>
              </div>
            </div>
          </section>

          {/* Early Prevention Is The Key */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Early Prevention Is The Key
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The key to surviving oral cancer is early detection and treatment. In its early stages, oral cancer is often painless and may not be noticeable to the untrained eye. However, a dental professional trained in oral cancer screening can identify suspicious lesions and abnormalities that may require further evaluation.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  During your regular dental check-up at Fort Worth Dental, your dentist will carefully examine all areas of your mouth, including your lips, tongue, gums, floor and roof of your mouth, inside of your cheeks, and throat. We look for any lumps, bumps, discolorations, or unusual tissue changes that could indicate the early stages of oral cancer.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Early detection means that treatment can begin before the cancer spreads, significantly improving the chances of successful treatment and survival. That's why we recommend regular dental visits every six months – not just for your teeth and gums, but for comprehensive oral health screening, including oral cancer detection.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/oral-cancer-new-2.jpg" 
                  alt="Early Cancer Prevention" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
            </div>
          </section>

          {/* How Can It Be Spotted Early */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              How Can Oral Cancer Be Spotted Early?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              During an oral cancer screening, your Fort Worth dentist will look for several warning signs:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Red or white patches in the mouth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Sores that bleed easily or don't heal within two weeks</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>A lump or thickening in the cheek, tongue, or neck</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Difficulty swallowing, chewing, or moving the jaw or tongue</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>A persistent sore throat or feeling that something is caught in the throat</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Numbness of the tongue or other areas of the mouth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Swelling of the jaw that causes dentures to fit poorly or become uncomfortable</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Chronic hoarseness or change in voice</span>
              </li>
            </ul>
            <p className="text-gray-700 text-lg leading-relaxed mt-6">
              If any suspicious areas are found, your dentist may recommend a biopsy or refer you to a specialist for further evaluation. Remember, finding something unusual doesn't necessarily mean you have cancer – many oral abnormalities are benign. However, it's always better to have any concerning areas checked promptly.
            </p>
          </section>

          {/* Risk Factors */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Who Is At Risk For Oral Cancer?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              While anyone can develop oral cancer, certain factors increase your risk:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Tobacco use (cigarettes, cigars, pipes, chewing tobacco)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Heavy alcohol consumption</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>HPV (Human Papillomavirus) infection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Excessive sun exposure (lip cancer)</span>
                </li>
              </ul>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Age (most cases occur in people over 40)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Gender (men are twice as likely as women)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Poor diet lacking in fruits and vegetables</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span>Weakened immune system</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Oral Cancer Screening?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Oral cancer screenings are included in every routine dental examination at no additional charge.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Comprehensive Screening</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Comprehensive Oral Exam Today</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for your oral cancer screening in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Oral Cancer Screening in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default OralCancerScreening;